import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  FailureNotice,
  type HeatStrings,
  heatMessages,
} from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  ENERGY_UNITS,
  HEAT_MASS_UNITS,
  LATENT_HEAT_UNITS,
  unitFactor,
  unitOptionsOf,
  unitSymbol,
} from "@/lib/tool-kit/chem-physical/heat";
import { LATENT_HEATS } from "@/lib/tool-kit/chem-physical/heat-data";
import {
  LATENT_PROCESSES,
  type LatentProcess,
  type LatentSolution,
  type LatentTerm,
  processSign,
  solveLatent,
  tableLatentHeat,
  tableTransitionPoint,
} from "@/lib/tool-kit/chem-physical/heat-modes";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

const CUSTOM = "custom";
const DEFAULT_SIG = 4;
const TERMS: LatentTerm[] = ["q", "m", "l"];

function LatentAnswer({
  solution,
  texts,
  units,
  sig,
  substance,
  strings,
}: {
  solution: LatentSolution;
  texts: Record<LatentTerm, string>;
  units: { q: string; m: string; l: string };
  sig: number;
  substance: string;
  strings: HeatStrings;
}) {
  const fmt = useToolNumber();
  const { unknown, values, process } = solution;
  const p = (value: number) => formatPlain(fmt, value);
  const lists = { q: ENERGY_UNITS, m: HEAT_MASS_UNITS, l: LATENT_HEAT_UNITS };
  const labels: Record<LatentTerm, string> = {
    q: strings.latentQ,
    m: strings.termM,
    l: strings.latentL,
  };
  const answerText = `${formatSig(fmt, solution.answer, sig)} ${unitSymbol(lists[unknown], units[unknown])}`;
  const base = { q: "J", m: "g", l: "J/g" };
  const conversions = TERMS.filter(
    (term) => term !== unknown && unitFactor(lists[term], units[term]) !== 1
  ).map((term) =>
    t(strings.convertLine, {
      label: labels[term],
      value: texts[term].trim(),
      unit: unitSymbol(lists[term], units[term]),
      converted: `${p(values[term])} ${base[term]}`,
    })
  );
  const rearranged = {
    q: strings.latentRearrangedQ,
    m: strings.latentRearrangedM,
    l: strings.latentRearrangedL,
  }[unknown];
  const substituted = t(
    {
      q: strings.latentSubstitutedQ,
      m: strings.latentSubstitutedM,
      l: strings.latentSubstitutedL,
    }[unknown],
    {
      q: p(values.q),
      m: p(values.m),
      l: p(values.l),
      result: `${formatSig(fmt, values[unknown], sig + 1)} ${base[unknown]}`,
    }
  );
  const point = tableTransitionPoint(substance, process);
  const signed = processSign(process) === 1;
  const needsSign = unknown === "q" && !signed;
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answerText} />
        <StatCard
          label={strings.direction}
          value={signed ? strings.absorbed : strings.released}
        />
        {point === null ? null : (
          <StatCard
            label={strings.transitionTemperature}
            value={`${formatPlain(fmt, point)} °C`}
          />
        )}
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.latentEquation}</p>
        <p>{rearranged}</p>
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{substituted}</p>
        {needsSign ? <p>{strings.latentSignNote}</p> : null}
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: labels[unknown],
          value: answerText,
        })}
      />
    </>
  );
}

export function HeatLatentPanel({
  strings,
  sigChoice,
}: {
  strings: HeatStrings;
  sigChoice: string;
}) {
  const fmt = useToolNumber();
  const [unknown, setUnknown] = useState<LatentTerm>("q");
  const [process, setProcess] = useState<LatentProcess>("melt");
  const [substance, setSubstance] = useState("water");
  const [text, setText] = useState<Record<LatentTerm, string>>({
    q: "",
    m: "100.0",
    l: "",
  });
  const [unit, setUnit] = useState({ q: "kJ", m: "g", l: "Jg" });

  const table =
    substance === CUSTOM ? null : tableLatentHeat(substance, process);
  const lText =
    table === null
      ? text.l
      : formatPlain(fmt, table / unitFactor(LATENT_HEAT_UNITS, unit.l));
  const texts = { ...text, l: lText };
  const outcome = solveLatent({ unknown, process, text: texts, unit });
  const typed = TERMS.filter((term) => term !== unknown).map(
    (term) => texts[term]
  );
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);

  const labels: Record<LatentTerm, string> = {
    q: strings.latentQ,
    m: strings.termM,
    l: strings.latentL,
  };
  const processNames: Record<LatentProcess, string> = {
    melt: strings.processMelt,
    freeze: strings.processFreeze,
    boil: strings.processBoil,
    condense: strings.processCondense,
  };
  const unitLabels = {
    q: strings.unitEnergy,
    m: strings.unitMass,
    l: strings.unitLatentHeat,
  };
  const unitList = {
    q: unitOptionsOf(ENERGY_UNITS),
    m: unitOptionsOf(HEAT_MASS_UNITS),
    l: unitOptionsOf(LATENT_HEAT_UNITS),
  };
  return (
    <>
      <PillGroup
        label={strings.processLabel}
        name="shl-process"
        onChange={setProcess}
        options={LATENT_PROCESSES.map((id) => ({
          value: id,
          label: processNames[id],
        }))}
        value={process}
      />
      <PillGroup
        label={strings.solveLabel}
        name="shl-solve"
        onChange={setUnknown}
        options={TERMS.map((term) => ({ value: term, label: labels[term] }))}
        value={unknown}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={strings.latentSubstanceHint}
          id="shl-substance"
          label={strings.substanceLabel}
          onChange={setSubstance}
          options={[
            { value: CUSTOM, label: strings.substanceCustom },
            ...LATENT_HEATS.map((entry) => ({
              value: entry.id,
              label:
                strings.substances[entry.id as keyof typeof strings.substances],
            })),
          ]}
          value={substance}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {TERMS.map((term) => (
          <QuantityInput
            id={`shl-${term}`}
            key={term}
            label={
              unknown === term
                ? t(strings.answerSuffix, { label: labels[term] })
                : labels[term]
            }
            onUnitChange={(value) => setUnit({ ...unit, [term]: value })}
            onValueChange={(value) => {
              if (term === "l") {
                setSubstance(CUSTOM);
              }
              setText({ ...text, [term]: value });
            }}
            solved={unknown === term}
            unit={unit[term]}
            unitLabel={unitLabels[term]}
            units={unitList[term]}
            value={texts[term]}
          />
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <LatentAnswer
            sig={sig}
            solution={outcome}
            strings={strings}
            substance={substance}
            texts={texts}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={heatMessages(strings)}
          />
        )}
        {unknown !== "q" && outcome.ok ? (
          <Notice>{strings.latentMagnitudeNote}</Notice>
        ) : null}
      </section>
    </>
  );
}
